import React, { useCallback, useEffect, useState } from 'react';
import PatientDetails from '../patient/PatientDetails';
import InsuranceDetails from '../insurance/InsuranceDetails';
import IndeterminateCheckBox from '../components/IndeterminateCheckBox';
import SectionWell from '../confirm/SectionWell';

/**
 * Displays a filterable table of patient details.
 *
 * @param patientList - The list of patient details. Each element in the patientList is expected to
 *                      have an id, a gearPatient, and an optional gearInsurance.
 * @param onDelete - The function to call when rows are deleted.
 * @param displayGdxId - Whether the GDX ID column should be included.
 */
export default function PatientList({ patientList, onDelete, displayGdxId }) {
	const [selectedRows, setSelectedRows] = useState([]);
	const [patientFilter, setPatientFilter] = useState('');

	/**
	 * Update the selected rows state when selection is toggled on or off for a row.
	 *
	 * This method is created with useCallback so a new method does not get created each time this component is
	 *  rendered. That is important because the PatientRow component uses this method in a useEffect dependency
	 *  array. If this method is recreated on each render, that useEffect would trigger on each render, which we don't
	 *  want.
	 *
	 * @param rowId The ID of the row being selected/deselected
	 */
	const selectPatient = useCallback(
		(rowId) => {
			// Use the functional update form of setState so we don't create a new selectPatient function each time the selected rows change
			setSelectedRows((prevSelectedRows) => {
				let newSelectionList = [...prevSelectedRows];
				let isAllSelected = newSelectionList.length === patientList.length || newSelectionList[0] === 'all';
				let isAnySelected = newSelectionList.length;
				const rowIndex = newSelectionList.indexOf(rowId);
				if (rowId === 'all') {
					if (isAnySelected) {
						// Deselect all
						newSelectionList = [];
					} else {
						// Select all
						newSelectionList = ['all'];
					}
				} else {
					if (isAllSelected) {
						// Uncheck this one
						newSelectionList = patientList
							.filter((result) => result.id !== rowId)
							.map((result) => result.id);
					} else if (rowIndex > -1) {
						// Remove item
						newSelectionList.splice(rowIndex, 1);
					} else {
						// Add item
						newSelectionList.push(rowId);
					}
				}

				return newSelectionList;
			});
		},
		[patientList]
	);

	/**
	 * Pass the selected IDs to the onDelete method and clear the selected rows state value.
	 */
	const deleteSelected = (evt) => {
		evt.preventDefault();
		if (!selectedRows.length || !patientList || !onDelete) {
			return;
		}
		let toDelete = selectedRows[0] === 'all' ? patientList.map((result) => result.id) : selectedRows;

		onDelete(toDelete);
		setSelectedRows([]);
	};

	if (!patientList || !patientList.length) {
		return null;
	}

	// Some items are selected, but not all
	const indeterminateSelection =
		selectedRows.length && selectedRows.length !== patientList.length && selectedRows[0] !== 'all';

	return (
		<table className="table table-hover table-striped">
			<caption style={{ paddingLeft: '15px' }}>
				<div style={{ marginTop: '20px', display: 'inline-block' }}>
					<IndeterminateCheckBox
						indeterminate={indeterminateSelection}
						type="checkbox"
						className="scale-1_5"
						checked={selectedRows.length}
						onChange={() => selectPatient('all')}
						onClick={(evt) => evt.stopPropagation()}
					/>
					{!!selectedRows.length && (
						<span
							className="text-danger glyphicon glyphicon-trash cursor-pointer"
							style={{ marginLeft: '25px', fontSize: '1.5em', marginTop: '-5px' }}
							title="Delete selected patients"
							onClick={deleteSelected}
						></span>
					)}
				</div>
				<div className="col-xs-11 pull-right">
					<input
						type="text"
						value={patientFilter}
						className="form-control"
						onChange={(evt) => setPatientFilter(evt.target.value)}
						placeholder="Search by Name or Date of Birth"
					/>
				</div>
			</caption>
			<thead>
				<tr>
					<th width="1"></th>
					{displayGdxId && <th width="1%" style={{whiteSpace: 'nowrap'}}>GDX ID</th>}
					<th>Name</th>
					<th>Date of Birth</th>
					<th>Gender</th>
				</tr>
			</thead>
			<tbody>
				{patientList
					.filter((result) => result.gearPatient)
					.map((result) => (
						<React.Fragment key={result.id}>
							<PatientRow
								result={result}
								selected={selectedRows.includes(result.id) || selectedRows.includes('all')}
								selectPatient={selectPatient}
								filterText={patientFilter}
								displayGdxId={displayGdxId}
							/>
						</React.Fragment>
					))}
			</tbody>
		</table>
	);
}

/**
 * Displays a single patient row.
 */
const PatientRow = ({ result, selected, selectPatient, filterText, displayGdxId }) => {
	const [showDetails, setShowDetails] = useState(false);
	const [filterIncludes, setFilterIncludes] = useState(true);

	const firstName = result.gearPatient.nameFirst || '';
	const lastName = result.gearPatient.nameLast;
	const dateOfBirth = result.gearPatient.dateOfBirth;
	const filterLowerCase = filterText ? filterText.toLowerCase() : null;
	const resultId = result.id;

	/**
	 * Any time the filter changes, re-apply the filter to this row.
	 * Hide this row if any space-delimited piece of the filter does not match either
	 *  the first name, last name, or date of birth of this row.
	 * If this row was selected and the filter hides this row, un-select this row.
	 */
	useEffect(() => {
		// No filter was passed in, any row is valid.
		if (!filterLowerCase) {
			setFilterIncludes(true);
			return;
		}
		const patientDOB = dateOfBirth || '';

		// Strip out any blank filter pieces (Can happen when the filter begins or ends with spaces)
		const filterPieces = filterLowerCase.split(' ').filter(Boolean);
		let filterMatches = filterPieces.every(
			(filter) =>
				firstName.toLowerCase().startsWith(filter) ||
				lastName.toLowerCase().startsWith(filter) ||
				patientDOB.startsWith(filter)
		);

		if (!filterMatches) {
			// Remove this row as a selected row when hiding
			if (selected) {
				selectPatient(resultId);
			}
			setFilterIncludes(false);
		} else {
			setFilterIncludes(true);
		}
	}, [filterLowerCase, firstName, lastName, dateOfBirth, resultId, selected, selectPatient]);

	/**
	 * Pass this row's ID into selectPatient to toggle this row selected or not
	 */
	const handleSelection = (evt) => {
		selectPatient(result.id);
	};

	/**
	 * Hide/Show the patient details section
	 */
	const toggleShowDetails = () => {
		setShowDetails(!showDetails);
	};

	/**
	 * Do not display the row if it does not match the patient filter
	 */
	if (!filterIncludes) {
		return null;
	}

	return (
		<>
			<tr>
				<td>
					<input
						type="checkbox"
						checked={selected}
						className="scale-1_5"
						onChange={handleSelection}
						// onClick={(evt) => evt.stopPropagation()}
					/>
				</td>
				{displayGdxId && <td>{result.gsdlNo}</td>}
				<td onClick={toggleShowDetails} style={{ cursor: 'pointer' }}>
					<span className={'mr-5 glyphicon glyphicon-triangle-' + (showDetails ? 'bottom' : 'right')}></span>
					{firstName} {lastName}
				</td>
				<td onClick={toggleShowDetails} style={{ cursor: 'pointer' }}>
					{dateOfBirth || ''}
				</td>
				<td onClick={toggleShowDetails} style={{ cursor: 'pointer' }}>
					{result.gearPatient.gender || ''}
				</td>
			</tr>
			{showDetails && <PatientRowDetailed result={result} />}
		</>
	);
};

/**
 * Display the full details of a Patient
 *
 * @param result - The Patient data, containing patient and insurance details
 */
const PatientRowDetailed = ({ result }) => (
	<tr>
		<td colSpan="5">
			<PatientDetails patient={result.gearPatient} />
			{result.gearInsurance && (
				<SectionWell sectionId='insuranceInfo' title='Insurance Information'>
					<InsuranceDetails insuranceInfo={result.gearInsurance} patientInfo={result.gearPatient} />
				</SectionWell>
			)}
		</td>
	</tr>
);
